import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import storeApi from '../../services/storeApi';

const Header = () => {
  const navigate = useNavigate();
  const [isSticky, setIsSticky] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [cart, setCart] = useState(null);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch categories for navigation
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await storeApi.getCategories({ parent_only: 'true' });
        const categoriesData = Array.isArray(response) ? response : (response.results || []);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Initialize nice-select when categories are loaded
  useEffect(() => {
    if (!loadingCategories && categories.length >= 0) {
      // Wait for jQuery and nice-select to be available
      const initNiceSelect = () => {
        if (window.jQuery && typeof window.jQuery.fn.niceSelect === 'function') {
          const selectElement = document.querySelector('.select-search-category');
          if (selectElement) {
            // Destroy existing nice-select instance if it exists
            try {
              const $select = window.jQuery(selectElement);
              const $niceSelect = $select.next('.nice-select');
              if ($niceSelect.length) {
                $niceSelect.remove();
              }
              $select.css('display', '');
            } catch (e) {
              // Ignore errors
            }
            
            // Initialize nice-select with proper closed state
            setTimeout(() => {
              if (window.jQuery && typeof window.jQuery.fn.niceSelect === 'function') {
                const $select = window.jQuery('.select-search-category');
                
                // Initialize
                $select.niceSelect();
                
                // Immediately force closed state (no delay)
                const $niceSelectWrapper = $select.next('.nice-select');
                if ($niceSelectWrapper.length) {
                  // Remove any 'open' class that might have been added
                  $niceSelectWrapper.removeClass('open');
                  
                  // Force hide the list
                  const $list = $niceSelectWrapper.find('.list');
                  $list.hide();
                  $list.css({
                    'display': 'none',
                    'opacity': '0',
                    'visibility': 'hidden'
                  });
                  
                  // Prevent click event bubbling during initialization
                  $niceSelectWrapper.off('click.init');
                }
              }
            }, 100);
          }
        } else {
          // Retry if jQuery/nice-select not ready yet
          setTimeout(initNiceSelect, 100);
        }
      };
      
      initNiceSelect();
    }
  }, [categories, loadingCategories]);

  // Fetch cart and wishlist data
  const fetchCartData = useCallback(async () => {
    try {
      const cartData = await storeApi.getCart();
      // Ensure cart has proper structure with total_price
      if (cartData && !cartData.total_price && cartData.items) {
        // Calculate total if not provided by API
        const calculatedTotal = cartData.items.reduce((sum, item) => {
          const itemPrice = parseFloat(item.product?.price || 0);
          const itemQuantity = parseInt(item.quantity || 0);
          return sum + (itemPrice * itemQuantity);
        }, 0);
        cartData.total_price = calculatedTotal;
      }
      setCart(cartData || { items: [], total_price: 0 });
    } catch (error) {
      // User might not be logged in, set empty cart
      setCart({ items: [], total_price: 0 });
    }
  }, []);

  const fetchWishlistCount = useCallback(async () => {
    try {
      const wishlistData = await storeApi.getWishlist();
      setWishlistCount(wishlistData.count || 0);
    } catch (error) {
      // User might not be logged in
      setWishlistCount(0);
    }
  }, []);

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('access_token');
      setIsAuthenticated(!!token);
    };
    checkAuth();
    // Listen for storage changes (e.g., login from another tab)
    window.addEventListener('storage', checkAuth);
    // Also listen for custom login event
    const handleLogin = () => checkAuth();
    window.addEventListener('userLoggedIn', handleLogin);
    const handleLogout = () => {
      setIsAuthenticated(false);
      setCart({ items: [], total_price: 0 });
      setWishlistCount(0);
    };
    window.addEventListener('userLoggedOut', handleLogout);
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('userLoggedOut', handleLogout);
    };
  }, []);

  useEffect(() => {
    fetchCartData();
    fetchWishlistCount();
    // Update auth status after cart fetch
    const token = localStorage.getItem('access_token');
    setIsAuthenticated(!!token);
  }, [location.pathname, fetchCartData, fetchWishlistCount]); // Refetch when route changes

  // Listen for cart updates from other components
  useEffect(() => {
    const handleCartUpdate = () => {
      fetchCartData();
    };

    const handleWishlistUpdate = () => {
      fetchWishlistCount();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('wishlistUpdated', handleWishlistUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
    };
  }, [fetchCartData, fetchWishlistCount]);


  const imagePath = (path) => `/store/assets/images/${path}`;

  // Group categories for megamenu (show top 6-8 main categories)
  const mainCategories = categories.slice(0, 8);
  const remainingCategories = categories.slice(8);

  const handleLogoutClick = (e) => {
    e.preventDefault();
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setIsAuthenticated(false);
    setCart({ items: [], total_price: 0 });
    setWishlistCount(0);
    window.dispatchEvent(new Event('userLoggedOut'));
    navigate('/store');
  };

  return (
    <header>
      <style>{`
        .hb-menu nav > ul > li.no-arrow > a::after {
          content: none !important;
        }
      `}</style>
      {/* Header Top Area */}
      <div className="header-top">
        <div className="container">
          <div className="row">
            <div className="col-lg-3 col-md-4">
              <div className="header-top-left">
                      <ul className="phone-wrap">
                        <li><span>Customer Support:</span><a href="tel:+923330490575">+92 333 0490575</a></li>
                      </ul>
              </div>
            </div>
            <div className="col-lg-9 col-md-8">
              <div className="header-top-right">
                <ul className="ht-menu">
                  <li>
                    <div className="ht-setting-trigger">
                      <span>Settings</span>
                    </div>
                    <div className="setting ht-setting">
                      <ul className="ht-setting-list">
                        {isAuthenticated ? (
                          <>
                            <li><Link to="/store/account">My Account</Link></li>
                            <li><Link to="/store/checkout">Checkout</Link></li>
                            <li>
                              <a
                                href="#"
                                onClick={handleLogoutClick}
                              >
                                Logout
                              </a>
                            </li>
                          </>
                        ) : (
                          <>
                            <li><Link to="/store/login-register">My Account</Link></li>
                            <li><Link to="/store/checkout">Checkout</Link></li>
                            <li><Link to="/store/login-register">Sign In</Link></li>
                          </>
                        )}
                      </ul>
                    </div>
                  </li>
                  <li>
                    <span className="currency-selector-wrapper">Currency :</span>
                    <div className="ht-currency-trigger">
                      <span>PKR ₨</span>
                    </div>
                    <div className="currency ht-currency">
                      <ul className="ht-setting-list">
                        <li className="active"><a href="#">PKR ₨</a></li>
                        <li><a href="#">USD $</a></li>
                        <li><a href="#">EUR €</a></li>
                      </ul>
                    </div>
                  </li>
                  <li>
                    <span className="language-selector-wrapper">Language :</span>
                    <div className="ht-language-trigger">
                      <span>English</span>
                    </div>
                    <div className="language ht-language">
                      <ul className="ht-setting-list">
                        <li className="active">
                          <a href="#">
                            <img src={imagePath('menu/flag-icon/1.jpg')} alt="" />English
                          </a>
                        </li>
                        <li>
                          <a href="#">
                            <img src={imagePath('menu/flag-icon/2.jpg')} alt="" />Français
                          </a>
                        </li>
                      </ul>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header Middle Area */}
      <div className="header-middle pl-sm-0 pr-sm-0 pl-xs-0 pr-xs-0">
        <div className="container">
          <div className="row">
                  <div className="col-lg-3">
                    <div className="logo pb-sm-30 pb-xs-30">
                      <Link to="/">
                        <img src="/images/logo.svg" alt="PakPoints Logo" style={{ height: '40px', width: 'auto' }} />
                      </Link>
                    </div>
                  </div>
            <div className="col-lg-9 pl-0 ml-sm-15 ml-xs-15">
              <form action="#" className="hm-searchbox" onSubmit={(e) => {
                e.preventDefault();
                const searchInput = e.target.querySelector('input[type="text"]');
                const categorySelect = e.target.querySelector('select');
                const searchTerm = searchInput.value.trim();
                const categorySlug = categorySelect.value;
                
                if (searchTerm) {
                  const params = new URLSearchParams();
                  if (categorySlug && categorySlug !== '0') {
                    params.append('category__slug', categorySlug);
                  }
                  params.append('search', searchTerm);
                  navigate(`/store/shop?${params.toString()}`);
                } else {
                  navigate(
                    categorySlug && categorySlug !== '0'
                      ? `/store/shop/${categorySlug}`
                      : '/store/shop'
                  );
                }
              }}>
                <select className="select-search-category react-nice-select" key={`category-select-${categories.length}`} data-react-select="true">
                  <option value="0">All Categories</option>
                  {categories.length > 0 ? (
                    categories.map(cat => (
                      <option key={cat.id} value={cat.slug}>{cat.name}</option>
                    ))
                  ) : (
                    <option value="0" disabled>Loading categories...</option>
                  )}
                </select>
                <input type="text" placeholder="Search for products..." />
                <button className="li-btn" type="submit">
                  <i className="fa fa-search"></i>
                </button>
              </form>
              <div className="header-middle-right">
                <ul className="hm-menu">
                  {isAuthenticated && (
                    <li className="hm-message">
                      <Link to="/messages" title="Messages" aria-label="Messages">
                        <i className="fa fa-envelope-o" aria-hidden="true" />
                      </Link>
                    </li>
                  )}
                  <li className="hm-wishlist">
                    <Link to="/store/wishlist">
                      <span className="cart-item-count wishlist-item-count">{wishlistCount}</span>
                      <i className="fa fa-heart-o"></i>
                    </Link>
                  </li>
                  <li className="hm-minicart">
                    <div className="hm-minicart-trigger">
                      <span className="item-icon"></span>
                      <span className="item-text">
                        PKR {cart?.total_price ? parseFloat(cart.total_price).toFixed(2) : '0.00'}
                        <span className="cart-item-count">{cart?.items && Array.isArray(cart.items) ? cart.items.length : 0}</span>
                      </span>
                    </div>
                    <div className="minicart">
                      {cart && cart.items && Array.isArray(cart.items) && cart.items.length > 0 ? (
                        <>
                          <ul className="minicart-product-list">
                            {cart.items.slice(0, 3).map(item => {
                              if (!item || !item.product) return null;
                              return (
                                <li key={item.id}>
                                  <Link to={`/store/product/${item.product.slug || item.product.id}`} className="minicart-product-image">
                                    <img 
                                      src={item.product.primary_image_url || imagePath('product/small-size/1.jpg')} 
                                      alt={item.product.name || 'Product'} 
                                    />
                                  </Link>
                                  <div className="minicart-product-details">
                                    <h6>
                                      <Link to={`/store/product/${item.product.slug || item.product.id}`}>
                                        {item.product.name || 'Product'}
                                      </Link>
                                    </h6>
                                    <span>PKR {item.product.price ? parseFloat(item.product.price).toFixed(2) : '0.00'} x {item.quantity || 1}</span>
                                  </div>
                                  <button 
                                    className="close" 
                                    title="Remove"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      try {
                                        await storeApi.removeFromCart(item.id);
                                        // Trigger cart update event
                                        window.dispatchEvent(new Event('cartUpdated'));
                                      } catch (error) {
                                        console.error('Error removing item:', error);
                                      }
                                    }}
                                  >
                                    <i className="fa fa-close"></i>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                          <p className="minicart-total">
                            SUBTOTAL: <span>PKR {cart.total_price ? parseFloat(cart.total_price).toFixed(2) : '0.00'}</span>
                          </p>
                          <div className="minicart-button">
                            <Link to="/store/cart" className="li-button li-button-fullwidth li-button-dark">
                              <span>View Full Cart</span>
                            </Link>
                            <Link to="/store/checkout" className="li-button li-button-fullwidth">
                              <span>Checkout</span>
                            </Link>
                          </div>
                        </>
                      ) : (
                        <div className="minicart-empty">
                          <p>Your cart is empty</p>
                          <Link to="/store/shop" className="li-button li-button-fullwidth">
                            <span>Continue Shopping</span>
                          </Link>
                        </div>
                      )}
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header Bottom Area */}
      <div className={`header-bottom header-sticky d-none d-lg-block d-xl-block ${isSticky ? 'sticky' : ''}`}>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="hb-menu">
                <nav>
                  <ul>
                    <li className="no-arrow">
                      <Link to="/store">Home</Link>
                    </li>
                    <li className="megamenu-holder">
                      <Link to="/store/shop">Shop</Link>
                      {!loadingCategories && categories.length > 0 && (
                        <ul className="megamenu hb-megamenu">
                          {mainCategories.length > 0 && (
                            <li>
                              <Link to="/store/shop">Shop by Category</Link>
                              <ul>
                                {mainCategories.map(cat => (
                                  <li key={cat.id}>
                                    <Link to={`/store/shop/${cat.slug}`}>
                                      {cat.name}
                                      {cat.product_count > 0 && ` (${cat.product_count})`}
                                    </Link>
                                  </li>
                                ))}
                                {remainingCategories.length > 0 && (
                                  <li>
                                    <Link to="/store/shop">View All Categories →</Link>
                                  </li>
                                )}
                              </ul>
                            </li>
                          )}
                          <li>
                            <Link to="/store/shop">Shop Pages</Link>
                            <ul>
                              <li><Link to="/store/shop">All Products</Link></li>
                              <li><Link to="/store/shop?featured_type=new_arrival">New Arrivals</Link></li>
                              <li><Link to="/store/shop?featured_type=bestseller">Bestsellers</Link></li>
                              <li><Link to="/store/shop?featured_type=sale">On Sale</Link></li>
                            </ul>
                          </li>
                        </ul>
                      )}
                    </li>
                    <li className="megamenu-static-holder">
                      <Link to={isAuthenticated ? "/store/account" : "/store/login-register"}>My Account</Link>
                      <ul className="megamenu hb-megamenu">
                        <li>
                          <Link to={isAuthenticated ? "/store/account" : "/store/login-register"}>Account & Orders</Link>
                          <ul>
                            {isAuthenticated ? (
                              <>
                                <li><Link to="/store/account">My Account</Link></li>
                                <li><Link to="/store/wishlist">My Wishlist</Link></li>
                                <li><Link to="/store/cart">Shopping Cart</Link></li>
                                <li><Link to="/store/checkout">Checkout</Link></li>
                              </>
                            ) : (
                              <>
                                <li><Link to="/store/login-register">Login / Register</Link></li>
                                <li><Link to="/store/login-register">My Account</Link></li>
                                <li><Link to="/store/wishlist">My Wishlist</Link></li>
                                <li><Link to="/store/cart">Shopping Cart</Link></li>
                                <li><Link to="/store/checkout">Checkout</Link></li>
                              </>
                            )}
                          </ul>
                        </li>
                        <li>
                          <Link to="/store">Help & Support</Link>
                          <ul>
                            <li><Link to="/store/about-us">About Us</Link></li>
                            <li><Link to="/store/contact">Contact Us</Link></li>
                            <li><Link to="/store/faq">FAQ</Link></li>
                            <li><Link to="/store/compare">Compare Products</Link></li>
                          </ul>
                        </li>
                      </ul>
                    </li>
                    <li className="no-arrow">
                      <Link to="/store/about-us">About Us</Link>
                    </li>
                    <li className="no-arrow">
                      <Link to="/store/contact">Contact</Link>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Area */}
      <div className="mobile-menu-area d-lg-none d-xl-none col-12">
        <div className="container">
          <div className="row">
            <div className="mobile-menu"></div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

